// Mobile Fullscreen Manager for Taronno's Love
// Provides immersive landscape fullscreen on mobile while keeping desktop 100% untouched.

export function isMobileDevice() {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isCoarse = window.matchMedia('(pointer: coarse)').matches;
  const smallScreen = window.innerWidth <= 1024 || window.innerHeight <= 1024;
  return hasTouch && (isCoarse || smallScreen);
}

export function isFullscreenActive() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.webkitCurrentFullScreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
}

export function requestMobileFullscreen() {
  if (!isMobileDevice()) return Promise.resolve(false);

  const el = document.documentElement;
  const requestMethod =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.webkitRequestFullScreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;

  if (requestMethod) {
    return requestMethod.call(el)
      .then(() => {
        // Request landscape lock alongside fullscreen where supported
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(() => {});
        }
        window.scrollTo(0, 0);
        setTimeout(() => {
          window.dispatchEvent(new Event('mobile-layout-refresh'));
        }, 100);
        return true;
      })
      .catch((err) => {
        // Silently handle if user or browser rejected fullscreen
        console.warn('Mobile fullscreen request not granted:', err);
        return false;
      });
  }

  // Fallback for browsers without standard fullscreen (e.g. older iOS Safari)
  window.scrollTo(0, 0);
  setTimeout(() => {
    window.dispatchEvent(new Event('mobile-layout-refresh'));
  }, 100);
  return Promise.resolve(false);
}

export function exitMobileFullscreen() {
  const exitMethod =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.webkitCancelFullScreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (exitMethod && isFullscreenActive()) {
    return exitMethod.call(document).catch(() => {});
  }
  return Promise.resolve();
}

export class MobileFullscreenManager {
  constructor() {
    this.reenterBtn = null;
    this.isMobile = isMobileDevice();
    this.boundOnFullscreenChange = this.onFullscreenChange.bind(this);
  }

  init() {
    // Only active on mobile devices; leave PC desktop completely untouched
    if (!this.isMobile) return;

    this.createReenterButton();

    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
      'resize',
      'orientationchange'
    ];
    events.forEach(evt => {
      window.addEventListener(evt, this.boundOnFullscreenChange);
    });

    // Initial check
    this.updateButtonVisibility();
  }

  createReenterButton() {
    if (this.reenterBtn) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'mobile-fs-reenter';
    btn.className = 'mobile-fs-reenter-btn hidden';
    btn.setAttribute('aria-label', 'Enter Fullscreen Mode');
    btn.innerHTML = `<span class="fs-icon">⛶</span><span class="fs-label">FULLSCREEN</span>`;

    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      requestMobileFullscreen();
    });

    document.body.appendChild(btn);
    this.reenterBtn = btn;
  }

  onFullscreenChange() {
    if (!this.isMobile) return;

    window.scrollTo(0, 0);

    // Refresh layout immediately and after layout settles
    window.dispatchEvent(new Event('mobile-layout-refresh'));
    setTimeout(() => {
      window.dispatchEvent(new Event('mobile-layout-refresh'));
    }, 150);

    this.updateButtonVisibility();
  }

  updateButtonVisibility() {
    if (!this.reenterBtn || !this.isMobile) return;

    const inFullscreen = isFullscreenActive();
    const isPortrait = window.innerHeight > window.innerWidth;

    // Show re-enter button only when in landscape AND not already in fullscreen
    if (!inFullscreen && !isPortrait) {
      this.reenterBtn.classList.remove('hidden');
    } else {
      this.reenterBtn.classList.add('hidden');
    }
  }

  destroy() {
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
      'resize',
      'orientationchange'
    ];
    events.forEach(evt => {
      window.removeEventListener(evt, this.boundOnFullscreenChange);
    });

    if (this.reenterBtn && this.reenterBtn.parentNode) {
      this.reenterBtn.parentNode.removeChild(this.reenterBtn);
      this.reenterBtn = null;
    }
  }
}

export const mobileFullscreenManager = new MobileFullscreenManager();
